import { formatNumber } from "common/utils/formatters"
import { createContext, FC, memo, useContext, useEffect, useRef } from "react"
import { InView } from "react-intersection-observer"

export const EasingFunctions = {
  // no easing, no acceleration
  linear: (t: number) => t,
  // accelerating from zero velocity
  easeInQuad: (t: number) => t * t,
  // decelerating to zero velocity
  easeOutQuad: (t: number) => t * (2 - t),
  // acceleration until halfway, then deceleration
  easeInOutQuad: (t: number) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t),
  // accelerating from zero velocity
  easeInCubic: (t: number) => t * t * t,
  // decelerating to zero velocity
  easeOutCubic: (t: number) => --t * t * t + 1,
  // acceleration until halfway, then deceleration
  easeInOutCubic: (t: number) =>
    t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1,
  // accelerating from zero velocity
  easeInQuart: (t: number) => t * t * t * t,
  // decelerating to zero velocity
  easeOutQuart: (t: number) => 1 - --t * t * t * t,
  // acceleration until halfway, then deceleration
  easeInOutQuart: (t: number) =>
    t < 0.5 ? 8 * t * t * t * t : 1 - 8 * --t * t * t * t,
  // accelerating from zero velocity
  easeInQuint: (t: number) => t * t * t * t * t,
  // decelerating to zero velocity
  easeOutQuint: (t: number) => 1 + --t * t * t * t * t,
  // acceleration until halfway, then deceleration
  easeInOutQuint: (t: number) =>
    t < 0.5 ? 16 * t * t * t * t * t : 1 + 16 * --t * t * t * t * t,
}

type Easing = keyof typeof EasingFunctions

type AnimatedNumberProps = {
  value: string
  delay?: number
  easing?: Easing
  maximumFractionDigits?: number
}

const AnimatedNumberContext = createContext<boolean>(false)

export const AnimatedNumberProvider: FC<any> = ({ children }) => {
  return (
    <InView threshold={0} triggerOnce>
      {({ inView, ref }) => (
        <div ref={ref}>
          <AnimatedNumberContext.Provider value={inView}>
            {children}
          </AnimatedNumberContext.Provider>
        </div>
      )}
    </InView>
  )
}

const AnimatedNumber = ({
  value,
  delay = 2000,
  easing = "easeInQuart",
  maximumFractionDigits = 0,
}: AnimatedNumberProps) => {
  const ref = useRef<HTMLElement>(null)
  const inView = useContext(AnimatedNumberContext)

  useEffect(() => {
    if (ref.current == null) return
    const val = +value
    if (isNaN(val) || val == 0) {
      ref.current.innerText = value
    } else if (inView) {
      const fn = EasingFunctions[easing]
      let start: number = null,
        handle: number,
        progress = 0
      const next = (timestamp) => {
        if (ref.current == null) return
        if (start == null) start = timestamp
        else progress = fn(Math.min(1, (timestamp - start) / delay))
        ref.current.innerText = formatNumber(
          val * progress,
          maximumFractionDigits
        )
        if (progress < 1) {
          handle = requestAnimationFrame(next)
        }
      }

      handle = requestAnimationFrame(next)
      return () => {
        cancelAnimationFrame(handle)
      }
    } else {
      ref.current.innerText = "0"
    }
  }, [inView, value, delay, easing, maximumFractionDigits])
  return <span ref={ref} />
}

export default memo(AnimatedNumber)
